import { useMemo, useRef, useState } from "react";
import { resolveDreamSearchQueryZip } from "@portal/app/_common/lib/dreamSearchRoutes";
import {
  DEFAULT_SEARCH_PAGE_SIZE,
  getDefaultDreamSearchVariables,
} from "../../utils/searchHelpers";
import { createGenericContext } from "@portal/app/_common/lib/createGenericContext";
import { useSearchParams } from "next/navigation";
import { Filters } from "@portal/app/_common/types/dreamSearch";
import {
  emptyFilters,
  useQueryParamFiltersForDreamSearch,
} from "../../utils/useQueryParamFiltersForDreamSearch";
import { DreamSearchForDreamSearchQueryVariables } from "@portal/app/health/_components/generated";

export type DreamSearchFilterContextProps = {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  clearAllFilters: () => void;
  variables: DreamSearchForDreamSearchQueryVariables;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
};

const [useDreamSearchFilterContext, DreamSearchFilterContext] =
  createGenericContext<DreamSearchFilterContextProps>("DreamSearchFilterContext");

export { useDreamSearchFilterContext };

export const DreamSearchFilterProvider = ({
  industry,
  vocation,
  onlyIncludePartnered,
  pinnedProgramId,
  initialGeoCenter,
  initialUserFilters,
  complianceExclusionTags,
  excludedProgramIds,
  shouldPromptForLocation = false,
  allowQueryZipOverride = false,
  children,
}: {
  industry?: GQL.VocationIndustry;
  vocation?: {
    id: string;
    industry?: GQL.VocationIndustry;
    name?: string;
  } | null;
  /**
   * When true, seeds provider_only_partnered=true into defaultVariables so the
   * filter survives client-side URL re-serialization (the query param is
   * excluded from URL sync by shouldSetQueryParam for privacy/compatibility).
   * Threaded from form funnel arrivals; see ENG-5978.
   */
  onlyIncludePartnered?: boolean;
  /** From `?pinned_program`. A ranking directive, not a filter, so it skips `filters` and URL sync. */
  pinnedProgramId?: string;
  initialGeoCenter: GQL.DreamSearchGeoCenter;
  initialUserFilters: Filters;
  complianceExclusionTags: string[];
  excludedProgramIds?: string[] | null;
  shouldPromptForLocation?: boolean;
  /** Results pages opt in; authored metro pages retain their server-selected location. */
  allowQueryZipOverride?: boolean;
  children:
    | NonNullable<React.ReactNode>
    | ((value: DreamSearchFilterContextProps) => React.ReactNode);
}) => {
  const searchParams = useSearchParams();
  const [page, setPage] = useState(0);
  const queryZips = searchParams?.getAll("zip");
  const queryZip = allowQueryZipOverride
    ? resolveDreamSearchQueryZip(queryZips?.length === 1 ? queryZips[0] : queryZips)
    : undefined;
  const clearAllRef = useRef(0);
  const geoCenter = useMemo(
    () => (queryZip && queryZip !== initialGeoCenter.zip ? { zip: queryZip } : initialGeoCenter),
    [queryZip, initialGeoCenter]
  );

  const defaultVariables = useMemo(
    () =>
      getDefaultDreamSearchVariables({
        ...(industry ? { industry } : {}),
        vocation,
        ...(onlyIncludePartnered && { onlyIncludePartnered: true }),
        geoCenter,
        first: DEFAULT_SEARCH_PAGE_SIZE,
        complianceExclusionTags,
        excludedProgramIds: excludedProgramIds ?? undefined,
      }),
    [
      industry,
      vocation,
      onlyIncludePartnered,
      geoCenter,
      complianceExclusionTags,
      excludedProgramIds,
    ]
  );

  const [filters, setFilters] = useQueryParamFiltersForDreamSearch(
    defaultVariables.dreamSearchFilters,
    initialUserFilters,
    { shouldPromptForLocation }
  );

  const context = useMemo(() => {
    return {
      filters,
      setFilters,
      clearAllFilters: () => {
        setFilters(emptyFilters);
        clearAllRef.current += 1;
      },
      variables: {
        dreamSearchFilters: {
          ...defaultVariables.dreamSearchFilters,
          ...Object.entries(filters).reduce((previousValue, [key, value]) => {
            // Protect the form-funnel-seeded vocation default from being wiped
            // by an empty state slot. Scoped to program_vocation_in so we don't
            // regress industry/other chip-removal behavior on non-funnel pages.
            // See ENG-5978.
            const defaultValue = (defaultVariables.dreamSearchFilters as Record<string, unknown>)[
              key
            ];
            if (
              key === "program_vocation_in" &&
              Array.isArray(value) &&
              value.length === 0 &&
              Array.isArray(defaultValue) &&
              defaultValue.length > 0
            ) {
              return previousValue;
            }
            return {
              ...previousValue,
              ...(value !== emptyFilters[key as keyof Filters] && { [key]: value }),
            };
          }, {}),
        },
        geo: {
          geoCenter,
        },
        first: defaultVariables.first,
        complianceExclusionTags: defaultVariables.complianceExclusionTags,
        // Only present when set, so unpinned variables stay byte-identical.
        ...(pinnedProgramId && { pinnedProgramId }),
      },
      page,
      setPage,
    };
  }, [
    defaultVariables.dreamSearchFilters,
    defaultVariables.first,
    defaultVariables.complianceExclusionTags,
    pinnedProgramId,
    filters,
    geoCenter,
    setFilters,
    page,
    setPage,
  ]);

  return (
    <DreamSearchFilterContext.Provider value={context} key={clearAllRef.current}>
      {typeof children === "function" ? children(context) : children}
    </DreamSearchFilterContext.Provider>
  );
};
